import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useStore from '../../store/useStore';
import { metricsAPI, alertsAPI, rollupsAPI } from '../../services/api';
import adminAPI from '../../services/adminAPI';
import AdminWidgetBuilder from '../AdminWidgetBuilder';
import ReportDisplay from '../widgets/ReportDisplay';
import FreshnessBadge from '../widgets/FreshnessBadge';
import { ROLLUP_AGGREGATIONS, ROLLUP_LABEL, WIDGET_TYPE_LABEL, currentOf, describeValue, formatDateTime } from './dataSourceFormat';
import '../../styles/DatasetManagement.css';
import '../../styles/DataSources.css';

const HISTORY_LIMIT = 200;

/**
 * One live metric: its latest value and recent history, how it is shown on
 * dashboards (the definition), whether it is kept as a KPI, and the alerts
 * watching it.
 */
const MetricDetailPage = () => {
  const { key } = useParams();
  const navigate = useNavigate();
  const companyId = useStore((state) => state.currentCompany?.id);
  const staleMinutes = useStore((state) => state.currentDashboard?.stale_threshold_minutes) || 5;
  const user = useStore((state) => state.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  const [state, setState] = useState({ loading: true, error: '', definition: null, latest: null, history: [], alerts: [] });
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState('');
  const [rollup, setRollup] = useState({ aggregation: '', cadence: 'day' });
  const [savingRollup, setSavingRollup] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');

  const describeError = (err, fallback) => err?.response?.data?.error || err?.message || fallback;

  const load = useCallback(async () => {
    if (!companyId) return;
    try {
      setState((s) => ({ ...s, loading: true, error: '' }));
      const ok = (p) => p.then((r) => (r?.success ? r.data : null)).catch(() => null);
      const [defs, history, rules] = await Promise.all([
        ok(metricsAPI.getDefinitions(companyId)),
        ok(metricsAPI.getHistory(key, { companyId, limit: HISTORY_LIMIT })),
        ok(alertsAPI.rules(companyId)),
      ]);
      const definition = (defs || []).find((d) => d.metric_key === key);
      if (!definition) throw new Error(`There is no live metric called ${key}`);
      const points = (history || [])
        .map((m) => ({ at: m.timestamp, value: Number(currentOf(m.value)), raw: m.value }))
        .sort((a, b) => new Date(a.at) - new Date(b.at));
      setState({
        loading: false,
        error: '',
        definition,
        latest: points.length ? points[points.length - 1] : null,
        history: points.filter((p) => Number.isFinite(p.value)).map((p) => ({ time: new Date(p.at).toLocaleString([], { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }), value: p.value })),
        alerts: (rules || []).filter((r) => r.kind === 'live' && r.metricKey === key),
      });
      setRollup({ aggregation: definition.rollup_aggregation || '', cadence: definition.rollup_cadence || 'day' });
    } catch (err) {
      setState((s) => ({ ...s, loading: false, error: describeError(err, 'Could not load the metric') }));
    }
  }, [companyId, key]);

  useEffect(() => { load(); }, [load]);

  const saveRollup = async () => {
    const def = state.definition;
    try {
      setSavingRollup(true);
      await rollupsAPI.set(def.id, rollup.aggregation || null, rollup.aggregation ? rollup.cadence : null);
      if (rollup.aggregation) {
        const res = await rollupsAPI.run(def.id);
        const rows = res?.data?.rows ?? 0;
        setMessage(`Tracking as a KPI: ${rows} completed ${rollup.cadence} period${rows === 1 ? '' : 's'} written to kpi_values.`);
      } else {
        setMessage('No longer tracked as a KPI.');
      }
      await load();
    } catch (err) {
      setState((s) => ({ ...s, error: describeError(err, 'Could not change KPI tracking') }));
    } finally {
      setSavingRollup(false);
    }
  };

  const remove = async () => {
    try {
      const res = await adminAPI.metricDefinitions.delete(state.definition.id);
      if (!res?.success) throw new Error(res?.error || 'Delete failed');
      navigate('/admin/data-sources', { replace: true, state: { notice: `Deleted live metric ${key}` } });
    } catch (err) {
      setState((s) => ({ ...s, error: describeError(err, 'Could not delete the metric') }));
    }
  };

  const { definition, latest, history, alerts } = state;
  const rollupChanged = definition && ((definition.rollup_aggregation || '') !== rollup.aggregation || (rollup.aggregation && (definition.rollup_cadence || 'day') !== rollup.cadence));
  const config = definition?.config || {};

  return (
    <div className="dataset-management data-source-detail">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/admin/data-sources">Data sources</Link> <span aria-hidden="true">›</span> <span>{definition?.display_name || key}</span>
      </nav>

      <div className="page-header">
        <div>
          <h2>{definition?.display_name || key}</h2>
          <p className="page-subtitle">Live metric · <code>{key}</code></p>
        </div>
        <div className="page-actions">
          {isAdmin && definition && <button className="btn-secondary" onClick={() => setEditing(true)}>Edit definition</button>}
          <button className="btn-primary" onClick={load}>Refresh</button>
        </div>
      </div>

      {state.error && <div className="error-message" role="alert">{state.error}</div>}
      {message && <div className="success-message" role="status">{message}<button className="error-dismiss" onClick={() => setMessage('')} aria-label="Dismiss">×</button></div>}

      {state.loading && !definition ? <div className="loading-spinner" /> : definition && (
        <>
          <dl className="source-summary">
            <div><dt>Latest value</dt><dd className="source-summary-value">{latest ? describeValue(latest.raw) : '—'}</dd></div>
            <div><dt>Last received</dt><dd>{formatDateTime(latest?.at)}</dd></div>
            <div><dt>Freshness</dt><dd>{latest ? <FreshnessBadge kind="live" at={latest.at} staleAfterMs={staleMinutes * 60 * 1000} /> : <span className="dataset-badge freshness-never" style={{ marginLeft: 0 }}>Never received</span>}</dd></div>
            <div><dt>Shown as</dt><dd>{WIDGET_TYPE_LABEL[definition.widget_type] || definition.widget_type}</dd></div>
          </dl>

          <section className="source-card" aria-labelledby="history-heading">
            <h3 id="history-heading">Recent values</h3>
            {history.length < 2 ? <p className="dataset-empty">Not enough history yet to draw a trend.</p> : (
              <div className="metric-history-chart widget report-widget">
                <div className="widget-body report-body"><ReportDisplay rows={history} config={{ displayType: 'line' }} /></div>
              </div>
            )}
            <p className="source-hint">Last {HISTORY_LIMIT} pushes. Older values are pruned after the company's retention period.</p>
          </section>

          <div className="dataset-detail-grid">
            <section className="source-card" aria-labelledby="definition-heading">
              <h3 id="definition-heading">On dashboards</h3>
              <dl className="dataset-meta">
                <dt>Display name</dt><dd>{definition.display_name || key}</dd>
                <dt>Widget type</dt><dd>{WIDGET_TYPE_LABEL[definition.widget_type] || definition.widget_type}</dd>
                {config.min !== undefined && <><dt>Range</dt><dd>{config.min} to {config.max}</dd></>}
                {Array.isArray(config.thresholds) && config.thresholds.length > 0 && <><dt>Thresholds</dt><dd>{config.thresholds.length} colour band{config.thresholds.length === 1 ? '' : 's'}</dd></>}
                {config.unit && <><dt>Unit</dt><dd>{config.unit}</dd></>}
              </dl>
              <p className="source-hint">These apply wherever the metric is shown. Individual widgets can override the title, unit and trend line in the dashboard editor.</p>
            </section>

            <section className="source-card" aria-labelledby="kpi-heading">
              <h3 id="kpi-heading">Keep as a KPI</h3>
              <p className="source-hint">Live values are short-lived. Tracking a metric as a KPI saves one value per day, week or month into the <code>kpi_values</code> dataset, so it can be reported on over time and given a target.</p>
              <div className="rollup-control">
                <select aria-label={`Track ${definition.display_name || key} as a KPI`} value={rollup.aggregation} disabled={!isAdmin} onChange={(e) => setRollup((r) => ({ ...r, aggregation: e.target.value }))}>
                  <option value="">Not tracked</option>
                  {ROLLUP_AGGREGATIONS.map((a) => <option key={a} value={a}>{ROLLUP_LABEL[a]}</option>)}
                </select>
                {rollup.aggregation && (
                  <select aria-label="KPI period" value={rollup.cadence} disabled={!isAdmin} onChange={(e) => setRollup((r) => ({ ...r, cadence: e.target.value }))}>
                    <option value="day">per day</option>
                    <option value="week">per week</option>
                    <option value="month">per month</option>
                  </select>
                )}
                {isAdmin && rollupChanged && <button type="button" className="btn-primary btn-small" disabled={savingRollup} onClick={saveRollup}>{savingRollup ? 'Saving…' : 'Save'}</button>}
              </div>
              {definition.rollup_last_run_at && <p className="source-hint">Last rolled up {formatDateTime(definition.rollup_last_run_at)}. It runs again every night.</p>}
            </section>
          </div>

          <section className="source-card" aria-labelledby="alerts-heading">
            <h3 id="alerts-heading">Alerts on this metric</h3>
            {alerts.length === 0 ? <p className="dataset-empty">None. <Link to="/alerts">Create an alert</Link> to be told when it crosses a threshold.</p> : (
              <ul className="used-by">
                {alerts.map((a) => (
                  <li key={a.id}>
                    <span className={`alert-state alert-state--${a.isActive ? a.state : 'off'}`}>{a.isActive ? a.state : 'off'}</span>
                    <Link to="/alerts">{a.name}</Link>
                    <span className="kpi-key">{a.operator} {Number(a.threshold).toLocaleString()}{a.durationSeconds ? ` for ${Math.round(a.durationSeconds / 60)} min` : ''}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {isAdmin && (
            <section className="source-card source-danger" aria-labelledby="delete-metric-heading">
              <h3 id="delete-metric-heading">Delete live metric</h3>
              <p>Removes the definition, its history and every widget showing it. If your sync tool keeps pushing it, it may be recreated.</p>
              <label>
                <span>Type <strong>{key}</strong> to confirm</span>
                <input value={confirmDelete} onChange={(e) => setConfirmDelete(e.target.value)} aria-label="Type the metric key to confirm deletion" />
              </label>
              <button type="button" className="btn-danger btn-small" disabled={confirmDelete !== key} onClick={remove}>Delete live metric</button>
            </section>
          )}
        </>
      )}

      {editing && (
        <AdminWidgetBuilder
          editingMetric={definition}
          onClose={() => setEditing(false)}
          onSuccess={async () => { setEditing(false); setMessage('Definition saved'); await load(); }}
        />
      )}
    </div>
  );
};

export default MetricDetailPage;
